import { Box } from '@chakra-ui/react';
import { CustomOnResize, CustomOnResizeStart } from 'modules/Editor/components/BoundingBox/BoundingBox.types';
import { ReactElement, useCallback, useEffect, useId, useRef, useState } from 'react';
import { isSideHandle } from 'utils/boundingBox';
import { generateShapeStyle } from 'widgets/Widget.helpers';
import { ReadOnlyWidgetBase, TransparentImg, WidgetBase, WidgetBaseProp, WidgetToolbar, useWidget } from 'widgets/sdk';
import { responsiveShapeResize } from 'widgets/store';

import { ONE_ONE_RATIO_SHAPES } from './BasicShapeWidget.config';
import { multiEditConfig } from './BasicShapeWidget.multiEdit';
import { BasicShapeLayoutProps, BasicShapeWidgetData } from './BasicShapeWidget.types';
import { BasicShapeWidgetToolbarMenu } from './BasicShapeWidgetToolbar';
import { SHAPE_RESIZE_MAP, SHAPE_TYPE_MAP } from './Shapes';
import { ResizeShapeOptions } from './Shapes/Shapes.types';

const COMPLEX_SHAPES_INDENT_SIZE = 31;

export const BasicShapeWidget = (): ReactElement => {
  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();
  const { widthPx, heightPx, border, type, widgetId } = useWidget<BasicShapeWidgetData>();
  const [dimension, setDimension] = useState({ width: widthPx, height: heightPx });

  const is1to1 = useRef(false);

  useEffect(() => {
    setDimension({ width: widthPx, height: heightPx });
  }, [widthPx, heightPx]);

  const resizeBasicShape = useCallback(
    (widthPx: number, heightPx: number, options?: ResizeShapeOptions) => {
      if (SHAPE_RESIZE_MAP[type]) {
        return SHAPE_RESIZE_MAP[type]({
          widthPx,
          heightPx,
          borderWidth: border.width,
          id,
          offset: COMPLEX_SHAPES_INDENT_SIZE,
          options,
        });
      }
    },
    [border.width, type, id],
  );

  // Add widget specific resize function for resizing the shape outside of this component
  // (e.g. for responsive groups)
  useEffect(() => {
    responsiveShapeResize.addResizeFn(widgetId, resizeBasicShape);

    return () => {
      responsiveShapeResize.clear(widgetId);
    };
  }, [widgetId, resizeBasicShape]);

  // Set the is1to1 flag when resizing starts
  const customResizeStart = useCallback(
    ({ event, onResizeStart }: CustomOnResizeStart) => {
      onResizeStart(event);
      is1to1.current = widthPx === heightPx && ONE_ONE_RATIO_SHAPES.includes(type);
    },
    [widthPx, heightPx, type],
  );

  const customOnResize = useCallback(
    ({ event, onResize, isGroup, responsiveResizeGroupData }: CustomOnResize) => {
      onResize(event);

      const widthPx = event.target.clientWidth;
      const heightPx = event.target.clientHeight;

      // 1:1 shapes in a responsive group widget should keep the aspect ratio if it's the bg shape
      let newWidthPx = widthPx;
      let newHeightPx = heightPx;
      const { direction } = event;
      if (isGroup && responsiveResizeGroupData.isBackgroundShape && is1to1.current && isSideHandle(direction)) {
        newHeightPx = newWidthPx;
        event.target.style.height = `${newHeightPx}px`;
      }

      // resize the shape without setting the new dimensions to the state
      resizeBasicShape(newWidthPx, newHeightPx);
    },
    [resizeBasicShape],
  );

  return (
    <WidgetBase onResizeStart={customResizeStart} onResize={customOnResize}>
      <WidgetToolbar multiEditConfig={multiEditConfig}>
        <BasicShapeWidgetToolbarMenu />
      </WidgetToolbar>
      <BasicShapeLayout id={id} dimension={dimension} offset={COMPLEX_SHAPES_INDENT_SIZE} />
    </WidgetBase>
  );
};

export const ReadOnlyBasicShapeWidget = ({ includeAltTextImg }: WidgetBaseProp): ReactElement => {
  // the id needs to be unique per component instance - as there can be multiple of the same widget on the same page -  widgetId will clash
  const id = useId();
  const { widthPx, heightPx } = useWidget<BasicShapeWidgetData>();
  const dimension = { width: widthPx, height: heightPx };

  return (
    <ReadOnlyWidgetBase>
      <BasicShapeLayout id={id} dimension={dimension} offset={COMPLEX_SHAPES_INDENT_SIZE} isReadOnly={true} />
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const BasicShapeLayout = ({ dimension, offset, isReadOnly, id }: BasicShapeLayoutProps): ReactElement => {
  const { type, border, fillColor, fillPercent, mirror, cornerRadius } = useWidget<BasicShapeWidgetData>();
  const { width, height } = dimension;
  const Shape = SHAPE_TYPE_MAP[type];

  const style = generateShapeStyle(mirror);

  return (
    <Box w={'100%'} h={'100%'} position={'absolute'}>
      <Shape
        id={id}
        fillColor={fillColor}
        fillPercent={fillPercent}
        widthPx={width}
        heightPx={height}
        border={border}
        style={style}
        offset={offset}
        isReadOnly={isReadOnly}
        {...(cornerRadius && { cornerRadius })}
      />
    </Box>
  );
};
